import { useState, useEffect } from "react";
import { useUpdateSaleMutation, useDeleteSaleMutation } from "./salesApiSlice";
import { useParams, useNavigate } from "react-router-dom";

let VAT_RATE = 0.11; // adjust if needed

function computeSaleDerived({ date, total, disctpct}) {
	// in this function we only allowed to recalculate derived fields from these inputs

    // if (isNaN(date.getTime())) {
    //     throw new Error('Invalid date format');
    // }

    const datePpnChanged = new Date('2023-04-01');
    if (date >= datePpnChanged) {
        VAT_RATE = 0.11;
    } else {
        VAT_RATE = 0.10;
    }

	const totalCalc = Number(total) || 0;
	const discPctCalc = Number(disctpct) || 0;

	const discount_sales = totalCalc * (discPctCalc / 100);
	const net_sales = totalCalc - discount_sales;
	const ppn = net_sales * VAT_RATE;
	const debet = net_sales + ppn;

	return {
		discount_sales,
		net_sales,
		ppn,
		debet,
	};
}


// Helper for date input
const toDateInput = (d) => {
	if (!d) return "";
	const dt = typeof d === "string" ? new Date(d) : d;
	if (Number.isNaN(dt.getTime())) return "";
	return dt.toISOString().slice(0, 10);
};

export default function EditSaleForm({ sale }) {
	const { id: routeId } = useParams();
	const navigate = useNavigate();
	const targetId = sale?._id || routeId;

	const [updateSale, { isLoading, isSuccess, isError, error }] = useUpdateSaleMutation();
	const [deleteSale, { isSuccess: isDelSuccess, isError: isDelError, error: delerror }] =
		useDeleteSaleMutation();

	// --- form state (prefill from sale) ---
	const [date, setDate] = useState(toDateInput(sale?.date));
	const [no_do, setNoDo] = useState(sale?.no_do || "");
	const [model_business, setModelBusiness] = useState(sale?.model_business || "");
	const [city, setCity] = useState(sale?.city || "");
	const [customer_category, setCustomerCategory] = useState(sale?.customer_category || "");
	const [customer_detail, setCustomerDetail] = useState(sale?.customer_detail || "");
	const [product_category, setProductCategory] = useState(sale?.product_category || "");
	const [product_type, setProductType] = useState(sale?.product_type || "");
	const [color, setColor] = useState(sale?.color || "");
	const [qty, setQty] = useState(sale?.qty ?? "");
	const [unit_price, setUnitPrice] = useState(sale?.unit_price ?? "");
	const [sales, setSales] = useState(sale?.sales || "");
	const [total, setTotal] = useState(sale?.total ?? "");
	const [disctpct, setDisctpct] = useState(sale?.disctpct ?? "");
	const [others, setOthers] = useState(sale?.others ?? "");
	const [no_po, setNoPo] = useState(sale?.no_po || "");
	const [price_list, setPriceList] = useState(sale?.price_list ?? "");
	const [main_company, setMainCompany] = useState(sale?.main_company || "");
	const [holding_type, setHoldingType] = useState(sale?.holding_type || "");
	const [sales_type, setSalesType] = useState(sale?.sales_type || "");
	const [province, setProvince] = useState(sale?.province || "");
	const [file_name, setFileName] = useState(sale?.file_name || "");
	const [user, setUser] = useState(sale?.user || "");

	// Derived fields when relevant inputs change
	const [discount_sales, setDiscountSales] = useState(sale?.discount_sales ?? 0);
	const [net_sales, setNetSales] = useState(sale?.net_sales ?? 0);
	const [ppn, setPpn] = useState(sale?.ppn ?? 0);
	const [debet, setDebet] = useState(sale?.debet ?? 0);

	// Validation (basic: required fields)
	const validDate = !!date;
	const validNoDo = no_do.trim().length > 0;
	const validQty = qty !== "" && !isNaN(Number(qty));
	const validUnitPrice = unit_price !== "" && !isNaN(Number(unit_price));
	const validSales = sales.trim().length > 0;
	const canSave =
		validDate &&
		validNoDo &&
		validQty &&
		validUnitPrice &&
		validSales &&
		!!targetId &&
		!isLoading;

	// After save/delete → go back
	useEffect(() => {
		if (isSuccess || isDelSuccess) {
		navigate("/dash/sales");
		}
	}, [isSuccess, isDelSuccess, navigate]);

	useEffect(() => {
    	const derived = computeSaleDerived({ date, total, disctpct });
		setDiscountSales(derived.discount_sales);
		setNetSales(derived.net_sales);
		setPpn(derived.ppn);
		setDebet(derived.debet);
	}, [discount_sales, net_sales, ppn, debet]);


  	const onSave = async (e) => {
		e.preventDefault();
		if (!canSave) return;
		try {
		await updateSale({
			id: targetId,
			date,
			no_do,
			model_business,
			city,
			customer_category,
			customer_detail,
			product_category,
			product_type,
			color,
			qty: Number(qty),
			unit_price: Number(unit_price),
			sales,
			total: Number(total),
			disctpct: Number(disctpct),
			discount_sales: Number(discount_sales),
			net_sales: Number(net_sales),
			ppn: Number(ppn),
			debet: Number(debet),
			others: Number(others),
			no_po,
			price_list: Number(price_list),
			main_company,
			holding_type,
			sales_type,
			province,
			file_name,
			user,
		}).unwrap();
		} catch {
		// handled by error banner
		}
	};

	const onDelete = async () => {
		if (!targetId) return;
		if (!window.confirm(`Delete sale record?`)) return;
		try {
		await deleteSale({ id: targetId }).unwrap();
		} catch {
		// handled by error banner
		}
	};

	const errText = (error?.data?.message || delerror?.data?.message) ?? "";

	return (
		<div className="min-h-full px-4 sm:px-6 lg:px-8 py-6">
		{(isError || isDelError) && (
			<div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
			{errText || "Something went wrong."}
			</div>
		)}

		<form onSubmit={(e) => e.preventDefault()} className="mt-2">
			<div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
				{/* Date */}
				<div>
				<label htmlFor="date" className="block text-sm font-medium text-gray-700">
					Date
				</label>
				<input
					id="date"
					type="date"
					value={date}
					onChange={(e) => setDate(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* No DO */}
				<div>
				<label htmlFor="no_do" className="block text-sm font-medium text-gray-700">
					No DO
				</label>
				<input
					id="no_do"
					type="text"
					value={no_do}
					onChange={(e) => setNoDo(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Model Business */}
				<div>
				<label htmlFor="model_business" className="block text-sm font-medium text-gray-700">
					Model Business
				</label>
				<input
					id="model_business"
					type="text"
					value={model_business}
					onChange={(e) => setModelBusiness(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* City */}
				<div>
				<label htmlFor="city" className="block text-sm font-medium text-gray-700">
					City
				</label>
				<input
					id="city"
					type="text"
					value={city}
					onChange={(e) => setCity(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Customer Category */}
				<div>
				<label htmlFor="customer_category" className="block text-sm font-medium text-gray-700">
					Customer Category
				</label>
				<input
					id="customer_category"
					type="text"
					value={customer_category}
					onChange={(e) => setCustomerCategory(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Customer Detail */}
				<div>
				<label htmlFor="customer_detail" className="block text-sm font-medium text-gray-700">
					Customer Detail
				</label>
				<input
					id="customer_detail"
					type="text"
					value={customer_detail}
					onChange={(e) => setCustomerDetail(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Product Category */}
				<div>
				<label htmlFor="product_category" className="block text-sm font-medium text-gray-700">
					Product Category
				</label>
				<input
					id="product_category"
					type="text"
					value={product_category}
					onChange={(e) => setProductCategory(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Product Type */}
				<div>
				<label htmlFor="product_type" className="block text-sm font-medium text-gray-700">
					Product Type
				</label>
				<input
					id="product_type"
					type="text"
					value={product_type}
					onChange={(e) => setProductType(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Color */}
				<div>
				<label htmlFor="color" className="block text-sm font-medium text-gray-700">
					Color
				</label>
				<input
					id="color"
					type="text"
					value={color}
					onChange={(e) => setColor(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Qty */}
				<div>
				<label htmlFor="qty" className="block text-sm font-medium text-gray-700">
					Quantity
				</label>
				<input
					id="qty"
					type="number"
					value={qty}
					onChange={(e) => setQty(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Unit Price */}
				<div>
				<label htmlFor="unit_price" className="block text-sm font-medium text-gray-700">
					Unit Price
				</label>
				<input
					id="unit_price"
					type="number"
					value={unit_price}
					onChange={(e) => setUnitPrice(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Sales */}
				<div>
				<label htmlFor="sales" className="block text-sm font-medium text-gray-700">
					Sales
				</label>
				<input
					id="sales"
					type="text"
					value={sales}
					onChange={(e) => setSales(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Total */}
				<div>
				<label htmlFor="total" className="block text-sm font-medium text-gray-700">
					Total
				</label>
				<input
					id="total"
					type="number"
					value={total}
					readOnly
					// onChange={(e) => setTotal(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Discount % */}
				<div>
				<label htmlFor="disctpct" className="block text-sm font-medium text-gray-700">
					Discount %
				</label>
				<input
					id="disctpct"
					type="number"
					value={disctpct}
					onChange={(e) => setDisctpct(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Discount Sales */}
				<div>
				<label htmlFor="discount_sales" className="block text-sm font-medium text-gray-700">
					Discount Sales
				</label>
				<input
					id="discount_sales"
					type="number"
					value={discount_sales}
					readOnly
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Net Sales */}
				<div>
				<label htmlFor="net_sales" className="block text-sm font-medium text-gray-700">
					Net Sales
				</label>
				<input
					id="net_sales"
					type="number"
					value={net_sales}
					readOnly
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* PPN */}
				<div>
				<label htmlFor="ppn" className="block text-sm font-medium text-gray-700">
					PPN
				</label>
				<input
					id="ppn"
					type="number"
					value={ppn}
					readOnly
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Debet */}
				<div>
				<label htmlFor="debet" className="block text-sm font-medium text-gray-700">
					Debet
				</label>
				<input
					id="debet"
					type="number"
					value={debet}
					readOnly
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Others */}
				<div>
				<label htmlFor="others" className="block text-sm font-medium text-gray-700">
					Others
				</label>
				<input
					id="others"
					type="number"
					value={others}
					readOnly
					// onChange={(e) => setOthers(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"		
				/>
				</div>
				{/* No PO */}
				<div>
				<label htmlFor="no_po" className="block text-sm font-medium text-gray-700">
					No PO
				</label>
				<input
					id="no_po"
					type="text"
					value={no_po}
					readOnly
					// onChange={(e) => setNoPo(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Price List */}
				<div>
				<label htmlFor="price_list" className="block text-sm font-medium text-gray-700">
					Price List
				</label>
				<input
					id="price_list"
					type="number"
					value={price_list}
					readOnly
					// onChange={(e) => setPriceList(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Main Company */}
				<div>
				<label htmlFor="main_company" className="block text-sm font-medium text-gray-700">
					Main Company
				</label>
				<input
					id="main_company"
					type="text"
					value={main_company}
					readOnly
					// onChange={(e) => setMainCompany(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Holding Type */}
				<div>
				<label htmlFor="holding_type" className="block text-sm font-medium text-gray-700">
					Holding Type
				</label>
				<input
					id="holding_type"
					type="text"
					value={holding_type}
					readOnly
					// onChange={(e) => setHoldingType(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Sales Type */}
				<div>
				<label htmlFor="sales_type" className="block text-sm font-medium text-gray-700">
					Sales Type
				</label>
				<input
					id="sales_type"
					type="text"
					value={sales_type}
					readOnly
					// onChange={(e) => setSalesType(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* Province */}
				<div>
				<label htmlFor="province" className="block text-sm font-medium text-gray-700">
					Province
				</label>
				<input
					id="province"
					type="text"
					value={province}
					readOnly
					// onChange={(e) => setProvince(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* File Name */}
				<div>
				<label htmlFor="file_name" className="block text-sm font-medium text-gray-700">
					File Name
				</label>
				<input
					id="file_name"
					type="text"
					value={file_name}
					readOnly
					// onChange={(e) => setFileName(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
				{/* User */}
				<div>
				<label htmlFor="user" className="block text-sm font-medium text-gray-700">
					User
				</label>
				<input
					id="user"
					type="text"
					value={user}
					readOnly
					// onChange={(e) => setUser(e.target.value)}
					className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900"
				/>
				</div>
			</div>

			{/* Footer actions */}
			<div className="mt-6 flex items-center justify-end gap-2">
				<button
				type="button"
				onClick={onDelete}
				className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
				>
				Delete
				</button>
				<button
				type="submit"
				onClick={onSave}
				disabled={!canSave}
				className={`rounded-xl px-4 py-2 text-sm text-white ${
					canSave ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
				}`}
				>
				Save
				</button>
			</div>
			</div>
		</form>
		</div>
	);
}
